import { toast } from "../stores/toast";

const PREFIX = "agnt-draft-v1:";
let scope: string | null = null;
const fallback = new Map<string, unknown>();
const volatile = new Set<string>();
let warned = false;
const listeners = new Set<() => void>();
export function subscribeDrafts(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}
const emit = () => { for (const listener of listeners) listener(); };

// Set only by the successful boot-time vault probe. Never trust a stale marker
// when the backend is unreachable, and retain other vaults' recovery data.
export function configureDraftScope(vault: string | null): void {
  scope = vault;
  warned = false;
  emit();
}
function storageKey(key: string): string {
  return PREFIX + JSON.stringify([scope, key]);
}
function warnUnavailable() {
  if (warned) return;
  warned = true;
  toast.warning("Draft recovery unavailable", "Keep this page open until you save. Browser storage or the vault identity is unavailable.");
}
export function readDraft<T>(key: string, valid?: (value: unknown) => value is T): T | undefined {
  const id = storageKey(key);
  let value: unknown;
  if (volatile.has(id)) value = fallback.get(id);
  else if (scope) {
    try {
      const raw = localStorage.getItem(id);
      if (!raw) return undefined;
      const record = JSON.parse(raw);
      if (record?.version !== 1 || !record.value || typeof record.value !== "object") return undefined;
      value = record.value;
    } catch { value = fallback.get(id); }
  } else value = fallback.get(id);
  return value !== undefined && (!valid || valid(value)) ? value as T : undefined;
}
export function writeDraft<T>(key: string, value: T): void {
  const id = storageKey(key);
  fallback.set(id, value);
  try {
    if (!scope) throw new Error("Vault not verified");
    localStorage.setItem(id, JSON.stringify({ version: 1, value, updatedAt: Date.now() }));
    volatile.delete(id);
  } catch { volatile.add(id); warnUnavailable(); }
  emit();
}
export function clearDraft(key: string): void {
  const id = storageKey(key);
  fallback.delete(id);
  try {
    localStorage.removeItem(id);
    volatile.delete(id);
  } catch {
    // A tombstone prevents old persisted content resurfacing in this session.
    volatile.add(id);
    warnUnavailable();
  }
  emit();
}
export function draftIsRecoverable(key: string): boolean {
  return Boolean(scope) && !volatile.has(storageKey(key));
}
export function moveDraft(from: string, to: string): void {
  if (from === to) return;
  const value = readDraft(from);
  if (value !== undefined) writeDraft(to, value);
  clearDraft(from);
}
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", (event) => {
    if ([...volatile].some((id) => fallback.has(id))) {
      event.preventDefault(); event.returnValue = "";
    }
  });
}

/** Enumerate only this verified vault's recovery records. */
export function draftKeys(): string[] {
  const ids = new Set(fallback.keys());
  try { for (let i = 0; i < localStorage.length; i++) { const key = localStorage.key(i); if (key?.startsWith(PREFIX)) ids.add(key); } } catch { /* in-memory recovery still works */ }
  return [...ids].flatMap(id => {
    try { const [vault, key] = JSON.parse(id.slice(PREFIX.length)); return vault === scope && typeof key === "string" ? [key] : []; }
    catch { return []; }
  });
}

/** A project rename changes the identity of all of its tasks too. */
export function moveProjectTaskDrafts(from: string, to: string): void {
  for (const key of draftKeys()) {
    if (key.startsWith("task:")) {
      try {
        const [project, slug] = JSON.parse(key.slice(5));
        if (project === from && typeof slug === "string") moveDraft(key, `task:${JSON.stringify([to, slug])}`);
      } catch { /* incompatible key; retain it */ }
    } else if (key.startsWith("task-create:")) {
      const value = readDraft<Record<string, unknown>>(key);
      if (value?.projectSlug === from) writeDraft(key, { ...value, projectSlug: to });
      if (key === `task-create:${from}`) moveDraft(key, `task-create:${to}`);
    }
  }
}
